import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, Legend
} from 'recharts';
import {
  TrendingUp, TrendingDown, Minus, Activity, Heart, Droplets,
  Thermometer, Footprints, Moon, Loader2, RefreshCw, AlertTriangle,
  CheckCircle, ChevronDown
} from 'lucide-react';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const METRICS = [
  { id: 'heart_rate', label: 'Heart Rate', unit: 'bpm', icon: Heart, color: '#ef4444', range: [60, 100] },
  { id: 'blood_pressure_systolic', label: 'BP Systolic', unit: 'mmHg', icon: Activity, color: '#3b82f6', range: [90, 120] },
  { id: 'blood_pressure_diastolic', label: 'BP Diastolic', unit: 'mmHg', icon: Activity, color: '#6366f1', range: [60, 80] },
  { id: 'blood_sugar', label: 'Blood Sugar', unit: 'mg/dL', icon: Droplets, color: '#f59e0b', range: [70, 100] },
  { id: 'spo2', label: 'SpO2', unit: '%', icon: Droplets, color: '#06b6d4', range: [95, 100] },
  { id: 'temperature', label: 'Temperature', unit: '°C', icon: Thermometer, color: '#f97316', range: [36.1, 37.2] },
  { id: 'steps', label: 'Daily Steps', unit: 'steps', icon: Footprints, color: '#22c55e', range: [5000, 10000] },
  { id: 'sleep_hours', label: 'Sleep', unit: 'hrs', icon: Moon, color: '#8b5cf6', range: [6, 9] },
];

function generateDemoData(metricId, days = 30) {
  const ranges = {
    heart_rate: { base: 72, variance: 8 },
    blood_pressure_systolic: { base: 118, variance: 10 },
    blood_pressure_diastolic: { base: 76, variance: 6 },
    blood_sugar: { base: 95, variance: 12 },
    spo2: { base: 97, variance: 2 },
    temperature: { base: 36.6, variance: 0.4 },
    steps: { base: 7500, variance: 3000 },
    sleep_hours: { base: 7.2, variance: 1.5 },
  };
  const cfg = ranges[metricId] || { base: 70, variance: 10 };
  const data = [];
  const now = new Date();
  for (let i = days; i >= 1; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    data.push({
      date: d.toISOString().split('T')[0],
      value: parseFloat((cfg.base + (Math.random() - 0.5) * 2 * cfg.variance).toFixed(1)),
    });
  }
  return data;
}

const TrendIcon = ({ direction }) => {
  if (direction === 'increasing') return <TrendingUp className="w-4 h-4 text-amber-500" />;
  if (direction === 'decreasing') return <TrendingDown className="w-4 h-4 text-blue-500" />;
  return <Minus className="w-4 h-4 text-green-500" />;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl border border-gray-700">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.stroke }}>
          {p.name}: {typeof p.value === 'number' ? p.value.toFixed(1) : p.value}
        </p>
      ))}
    </div>
  );
};

const HealthForecastChart = () => {
  const [selectedMetric, setSelectedMetric] = useState('heart_rate');
  const [forecastDays, setForecastDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [error, setError] = useState(null);
  const [showMetricDropdown, setShowMetricDropdown] = useState(false);

  const metric = METRICS.find(m => m.id === selectedMetric);

  const fetchForecast = useCallback(async () => {
    setLoading(true);
    setError(null);
    const historicalData = generateDemoData(selectedMetric, 30);

    try {
      const res = await fetch(`${API_URL}/api/ml-health/trend/advanced-forecast`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patient_id: 'current',
          metric: selectedMetric,
          historical_data: historicalData,
          forecast_days: forecastDays,
        }),
      });

      if (!res.ok) throw new Error('Forecast API returned an error');
      const data = await res.json();
      setForecastData(data);

      const combined = historicalData.map(d => ({
        date: d.date.slice(5),
        actual: d.value,
        forecast: null,
        lower: null,
        upper: null,
      }));

      const forecastDates = data.forecast?.dates || [];
      const forecastValues = data.forecast?.values || [];
      const lower = data.forecast?.confidence_lower || [];
      const upper = data.forecast?.confidence_upper || [];

      // bridge point
      if (combined.length > 0 && forecastValues.length > 0) {
        combined[combined.length - 1].forecast = combined[combined.length - 1].actual;
      }

      forecastDates.forEach((dt, i) => {
        const label = dt.slice(5, 10);
        combined.push({
          date: label,
          actual: null,
          forecast: parseFloat(forecastValues[i]?.toFixed(1)),
          lower: parseFloat(lower[i]?.toFixed(1)),
          upper: parseFloat(upper[i]?.toFixed(1)),
        });
      });

      setChartData(combined);
    } catch (err) {
      logger.error('Forecast error:', err);
      setError('Failed to generate forecast. Using demo data only.');
      setChartData(historicalData.map(d => ({ date: d.date.slice(5), actual: d.value })));
    }
    setLoading(false);
  }, [selectedMetric, forecastDays]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  const IconComp = metric?.icon || Activity;

  return (
    <div className="space-y-5" data-testid="health-forecast-section">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <button
            onClick={() => setShowMetricDropdown(!showMetricDropdown)}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-medium hover:border-gray-300 transition shadow-sm"
            data-testid="metric-selector"
          >
            <IconComp className="w-4 h-4" style={{ color: metric?.color }} />
            {metric?.label}
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
          {showMetricDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 w-56 py-1 max-h-64 overflow-y-auto">
              {METRICS.map(m => (
                <button
                  key={m.id}
                  onClick={() => { setSelectedMetric(m.id); setShowMetricDropdown(false); }}
                  className={`w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-gray-50 ${selectedMetric === m.id ? 'bg-gray-50 font-medium' : ''}`}
                  data-testid={`metric-option-${m.id}`}
                >
                  <m.icon className="w-4 h-4" style={{ color: m.color }} />
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {[7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setForecastDays(d)}
              className={`px-3 py-2 text-sm transition ${forecastDays === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
              data-testid={`forecast-days-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>

        <button
          onClick={fetchForecast}
          disabled={loading}
          className="flex items-center gap-1.5 bg-gray-900 text-white rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition shadow-sm"
          data-testid="refresh-forecast-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Analyzing...' : 'Refresh'}
        </button>
      </div>

      {/* Chart */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5" data-testid="forecast-chart-container">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <IconComp className="w-5 h-5" style={{ color: metric?.color }} />
            {metric?.label} Forecast
          </h3>
          {forecastData?.analysis && (
            <div className="flex items-center gap-3 text-sm">
              <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg">
                <TrendIcon direction={forecastData.analysis.trend_direction} />
                {forecastData.analysis.trend_direction}
              </span>
              {forecastData.analysis.seasonality_detected && (
                <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs">Seasonal</span>
              )}
              <span className="text-gray-400 text-xs">{forecastData.model_used}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={metric?.color || '#3b82f6'} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={metric?.color || '#3b82f6'} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorForecast" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorConfidence" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.08} />
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={45} domain={['auto', 'auto']} />
              <Tooltip content={<CustomTooltip />} />
              {metric?.range && (
                <>
                  <ReferenceLine y={metric.range[0]} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.5} />
                  <ReferenceLine y={metric.range[1]} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.5} />
                </>
              )}
              <Area type="monotone" dataKey="upper" stroke="none" fill="url(#colorConfidence)" name="Upper CI" />
              <Area type="monotone" dataKey="lower" stroke="none" fill="transparent" name="Lower CI" />
              <Area type="monotone" dataKey="actual" stroke={metric?.color || '#3b82f6'} fill="url(#colorActual)" strokeWidth={2} name="Actual" dot={false} connectNulls={false} />
              <Line type="monotone" dataKey="forecast" stroke="#f59e0b" strokeWidth={2.5} strokeDasharray="6 3" name="Forecast" dot={false} connectNulls={false} />
              <Legend verticalAlign="top" height={30} iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {error && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {error}
          </p>
        )}
      </div>

      {/* Recommendations */}
      {forecastData?.recommendations?.length > 0 && (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5" data-testid="forecast-recommendations">
          <h3 className="text-base font-semibold text-gray-900 mb-3">AI Recommendations</h3>
          <div className="space-y-2">
            {forecastData.recommendations.map((rec, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-xl text-sm ${
                rec.includes('ALERT') ? 'bg-red-50 text-red-800' :
                rec.includes('normal range') || rec.includes('Keep up') ? 'bg-green-50 text-green-800' :
                'bg-amber-50 text-amber-800'
              }`}>
                {rec.includes('ALERT') ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                 rec.includes('normal range') ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> :
                 <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <span>{rec}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Model Info */}
      {forecastData && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="forecast-model-info">
          <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
            <p className="text-xs text-gray-500">Model</p>
            <p className="text-sm font-medium text-gray-900 truncate">{forecastData.model_used}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
            <p className="text-xs text-gray-500">Trend</p>
            <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
              <TrendIcon direction={forecastData.analysis?.trend_direction} />
              {forecastData.analysis?.trend_direction}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
            <p className="text-xs text-gray-500">Strength</p>
            <p className="text-sm font-medium text-gray-900">{forecastData.analysis?.trend_strength?.toFixed(1)}%</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3 shadow-sm">
            <p className="text-xs text-gray-500">Seasonality</p>
            <p className="text-sm font-medium text-gray-900">{forecastData.analysis?.seasonality_detected ? 'Detected' : 'None'}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthForecastChart;
